/* PACKAGES */
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* ICONS */
import { MdOutlineArrowOutward } from "react-icons/md";
import BlogBox from "../../components/others/blogBox";

/* CONTAINER ANIMATION */
const containerAnimation = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 1 },
  },
};

/* DIV ANIMATION */
const animation = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
  hidden: {
    opacity: 0,
    y: 20,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
};

const BlogDetails = () => {
  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-transparent"
    >
      {/* PAGE TITLE */}
      <Helmet>
        <title>Gut Health | Aligned Rewards - OKR Software</title>
      </Helmet>

      <motion.div variants={animation} className="py-5 xl:py-12 lg:py-12">
        <div className="container">
          <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3">
            {/* BLOG CONTENT */}
            <div className="col-span-2 border-r-0 xl:border-r lg:border-r border-greyBorder">
              <div className="pr-0 xl:pr-10 lg:pr-10">
                <img
                  src="https://www.reyfeber.com/webfiles/blog/27042022120412Gut-Health-101-Blog-Cover-(1200-%C3%97-800px).png"
                  className="rounded-xl object-cover h-[20vh] xl:h-[40vh] lg:h-[40vh] w-full shadow"
                  alt="Gut Health 101: 5 ways you can improve your Gut Health"
                />

                <div className="mt-5">
                  <h1 className="mt-5 text-xl xl:text-3xl lg:text-3xl font-fraunces-bold tracking-tight text-black">
                    Gut Health 101: 5 ways you can improve your Gut Health
                  </h1>
                  <p className="mt-3 text-base font-source font-normal text-textGrey leading-relaxed">
                    Essentially, the term ‘Gut health’ means to maintain the
                    right balance of microorganisms that reside in your
                    digestive tract. Taking care of your gut and finding the
                    right balance of these microorganisms in your digestive
                    tract is crucial for not only your mental and physical
                    health but also for boosting your immunity.
                  </p>
                  <p className="mt-3 text-base font-source font-normal text-textGrey leading-relaxed">
                    Yes, there are thousands of kinds of bacteria that live
                    within your intestine, and each of them plays a significant
                    role in boosting your health. Usually, a diverse microbe is
                    said to be a healthy one. This is because the more kinds of
                    bacteria you have, the more health benefits they offer. One
                    can attain a diverse microbe set by consuming different
                    types of food.
                  </p>

                  <div className="mt-5">
                    <h5 className="text-lg xl:text-xl lg:text-xl font-source font-bold text-black">
                      5 ways that can help you to improve your gut health
                    </h5>
                    <div className="mt-5">
                      <p className="text-base xl:text-lg lg:text-lg font-source font-semibold text-black">
                        Consume more prebiotic fiber:
                      </p>
                      <p className="mt-3 text-base font-source font-normal text-textGrey leading-relaxed">
                        Research states that prebiotics can aid probiotics to
                        become more resilient to environmental conditions such
                        as temperature and pH changes. People who wish you
                        better their gut health can include more prebiotic-rich
                        food items in their diet. These foods can be, bananas,
                        asparagus, onions, garlic, and even whole grains.
                      </p>
                    </div>

                    <div className="mt-5">
                      <p className="text-base xl:text-lg lg:text-lg font-source font-semibold text-black">
                        Have fewer sweeteners and sugars:
                      </p>
                      <p className="mt-3 text-base font-source font-normal text-textGrey leading-relaxed">
                        Consuming large amounts of sugars or artificial sweaters
                        can cause an imbalance of gut microbes. Studies have
                        stated that a diet that is high in sugar & fat intake,
                        negatively impacts the gut microbe ecosystem that in
                        turn influences behavior. Instead of opting for sugars
                        and sweeteners, you can make the switch to healthier
                        alternatives such as honey, fruits, coconut sugar, maple
                        syrup, and more!
                      </p>
                    </div>

                    <div className="mt-5">
                      <p className="text-base xl:text-lg lg:text-lg font-source font-semibold text-black">
                        Say no to stress
                      </p>
                      <p className="mt-3 text-base font-source font-normal text-textGrey leading-relaxed">
                        Stress plays an important role in not only various
                        aspects of health but also in maintaining a healthy gut.
                        Research has stated that stress can disrupt the
                        microorganisms in the gut, even if the stress is for a
                        short duration. A few stressors can include,
                        environmental stress, psychological stress, or even
                        sleep deprivation.
                      </p>
                    </div>

                    <div className="mt-5">
                      <p className="text-base xl:text-lg lg:text-lg font-source font-semibold text-black">
                        Sleep for good hours
                      </p>
                      <p className="mt-3 text-base font-source font-normal text-textGrey leading-relaxed">
                        Getting good quality sleep is important for improving
                        your mood, cognition, and even gut health. Irregular
                        sleep and disturbed sleep may tend to negatively impact
                        the gut flora which in turn raises the risk of
                        inflammatory conditions
                      </p>
                    </div>

                    <div className="mt-5">
                      <p className="text-base xl:text-lg lg:text-lg font-source font-semibold text-black">
                        Include more vegetables in your diet
                      </p>
                      <p className="mt-3 text-base font-source font-normal text-textGrey leading-relaxed">
                        Studies have shown the vast difference between the gut
                        health of meat-eaters and those of vegetarians. It was
                        observed that a vegetarian diet contains a more diverse
                        set of microorganisms than a meat diet on account of the
                        high levels of prebiotic fibers.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* BLOG SIDEBAR */}
            <div className="col-span-1 mt-5 xl:mt-0 lg:mt-0">
              <div className="pl-0 xl:pl-10 lg:pl-10 sticky h-fit top-[15vh]">
                <div className="pb-10 border-b border-greyBorder">
                  <div className="bg-greyBg bg-opacity-50 border border-greyBorder rounded-lg p-5 xl:p-8 lg:p-8">
                    <h5 className="text-2xl font-fraunces-bold text-black tracking-tight">
                      Join Our Community
                    </h5>
                    <p className="text-base font-source font-normal text-greyText mt-3">
                      Join us so that we can send you updates on new features,
                      valuable tips, expert strategies and our exclusive offers
                      delivered directly to your inbox.
                    </p>
                    <form className="mt-5">
                      <input
                        type="email"
                        placeholder="Type you email address"
                        name="newsletterEmail"
                        id="newsletterEmail"
                        className="w-full block bg-white px-4 py-3 rounded-lg text-sm font-source font-semibold text-black border border-greyBorder focus:outline-brandColor placeholder:font-source placeholder:font-normal"
                      />
                      <Button className="group mt-3 w-full flex items-center gap-1 justify-center shadow-none hover:shadow-none font-source font-semibold text-white rounded-md bg-black normal-case text-sm hover:bg-opacity-80 transition-all duration-300 ease-in-out">
                        Subscribe
                        <MdOutlineArrowOutward className="w-4 h-4 text-white" />
                      </Button>
                    </form>
                  </div>
                </div>

                {/* RECENT BLOGS */}
                <div className="pt-10">
                  <BlogBox
                    image="https://www.reyfeber.com/webfiles/blog/27042022120412Gut-Health-101-Blog-Cover-(1200-%C3%97-800px).png"
                    title="Gut Health 101 - 5 ways you can improve your Gut Health"
                    desc="Essentially, the term ‘Gut health’ means to maintain the right balance of microorganisms that reside in your digestive tract...."
                    link="/blog-details"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default BlogDetails;
